import { useMemo } from "react";
import {
  Handle,
  Position,
  useInternalNode,
  type NodeProps,
} from "@xyflow/react";
import type { NodeStyle, InlineEntry, NodeShape } from "./types";
import { isNodeStyle } from "./node-style";
import { isSvgShape, renderShape } from "./shapes";
import { useAppearanceTheme } from "./appearance-context";
import { seedFromId } from "./sketch/seed";
import { sketchShapePaths, type SketchPath } from "./sketch/node-paths";
import { SketchSvg } from "./sketch/SketchSvg";

function isInlineEntries(value: unknown): value is InlineEntry[] {
  return (
    Array.isArray(value) &&
    value.every(
      (v) => typeof v === "object" && v !== null && "id" in v && "label" in v,
    )
  );
}

/** Render the list of collapsed inline child rows. */
function InlineChildrenList({
  children,
}: {
  readonly children: readonly InlineEntry[];
}) {
  return (
    <div
      style={{
        borderTop: "1px solid #4a4a6a",
        marginTop: 4,
        paddingTop: 4,
        fontSize: 11,
        color: "#9090a0",
      }}
    >
      {children.map((child) => (
        <div
          key={child.id}
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 8,
          }}
        >
          <span>{child.label}</span>
          {child.value && (
            <span
              style={{
                color: "#e0e0e0",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {child.value}
            </span>
          )}
        </div>
      ))}
    </div>
  );
}

/** Build the base CSS for the node container, merging style overrides. */
function buildNodeCss(
  shape: NodeShape | undefined,
  cssStyleProps: Omit<NodeStyle, "shape">,
  useSvg: boolean,
): React.CSSProperties {
  const css: React.CSSProperties = {
    padding: "8px 16px",
    borderRadius: shape === "rounded-rectangle" ? 16 : 4,
    // Longhand border properties only: NodeStyle.borderColor/borderWidth are
    // longhands, and mixing them with a `border` shorthand makes React warn
    // about conflicting style properties when the theme toggles.
    borderWidth: 1,
    borderStyle: "solid",
    borderColor: "var(--node-border, #4a4a6a)",
    background: "var(--node-bg, #1e1e2e)",
    color: "var(--node-text, #e0e0e0)",
    fontSize: 13,
    minWidth: 80,
    textAlign: "center" as const,
    ...cssStyleProps,
  };

  if (useSvg) {
    // SVG shape draws its own background and border; make the div transparent.
    css.background = "transparent";
    css.borderStyle = "none";
    css.borderRadius = 0;
    css.boxShadow = "none";
    css.position = "relative";
  }

  return css;
}

/** Render the SVG shape background when the node uses a non-rectangular shape. */
function ShapeOverlay({
  shape,
  cssStyleProps,
}: {
  readonly shape: NodeShape;
  readonly cssStyleProps: Omit<NodeStyle, "shape">;
}) {
  return renderShape(
    shape,
    cssStyleProps.background ?? "var(--node-bg, #1e1e2e)",
    cssStyleProps.borderColor ?? "var(--node-border, #4a4a6a)",
    typeof cssStyleProps.borderWidth === "number"
      ? cssStyleProps.borderWidth
      : 1,
  );
}

/**
 * Generate the sketchy paths for a node in hand-drawn mode. Returns
 * undefined when not in hand-drawn mode or before React Flow has measured
 * the node (first render pass) — callers fall back to crisp rendering, and
 * the measure cycle re-renders immediately after.
 */
function useSketchPaths(
  id: string,
  shape: NodeShape | undefined,
  cssStyleProps: Omit<NodeStyle, "shape">,
):
  | {
      readonly width: number;
      readonly height: number;
      readonly paths: SketchPath[];
    }
  | undefined {
  const theme = useAppearanceTheme();
  const internalNode = useInternalNode(id);
  const width = internalNode?.measured.width;
  const height = internalNode?.measured.height;
  const fill = cssStyleProps.background ?? "var(--node-bg, #1e1e2e)";
  const stroke = cssStyleProps.borderColor ?? "var(--node-border, #4a4a6a)";
  const strokeWidth =
    typeof cssStyleProps.borderWidth === "number"
      ? cssStyleProps.borderWidth
      : 1;

  return useMemo(() => {
    if (theme !== "hand-drawn" || width === undefined || height === undefined) {
      return undefined;
    }
    return {
      width,
      height,
      paths: sketchShapePaths({
        shape: shape ?? "rectangle",
        width,
        height,
        fill,
        stroke,
        strokeWidth,
        seed: seedFromId(id),
      }),
    };
  }, [theme, id, shape, width, height, fill, stroke, strokeWidth]);
}

/** Render the node label, with optional description tooltip. */
function NodeLabel({
  label,
  description,
}: {
  readonly label: string;
  readonly description: string | undefined;
}) {
  if (description) {
    return (
      <div className="diagram-label-hint" title={description}>
        {label}
      </div>
    );
  }
  return <div>{label}</div>;
}

/**
 * Default node component for the diagram renderer. Renders a labelled
 * box with source/target handles. Applies NodeStyle as inline CSS.
 */
export function DefaultNode({ id, data }: NodeProps) {
  const nodeStyle: NodeStyle | undefined = isNodeStyle(data.nodeStyle)
    ? data.nodeStyle
    : undefined;

  const shape: NodeShape | undefined = nodeStyle?.shape;
  // Separate shape from CSS-passthrough props so it doesn't leak into inline styles.
  const { shape: _shape, ...cssStyleProps } = nodeStyle ?? {};

  const inlineChildren: InlineEntry[] | undefined = isInlineEntries(
    data.inlineChildren,
  )
    ? data.inlineChildren
    : undefined;

  const description =
    typeof data.description === "string" && data.description.length > 0
      ? data.description
      : undefined;

  // In hand-drawn mode (with measured dimensions available) every shape is
  // drawn as a sketchy SVG overlay; the CSS border/background is suppressed
  // exactly like for the crisp SVG shapes.
  const sketch = useSketchPaths(id, shape, cssStyleProps);

  const useSvg = isSvgShape(shape);
  const suppressCssBox = useSvg || sketch !== undefined;
  const css = buildNodeCss(shape, cssStyleProps, suppressCssBox);
  const label = typeof data.label === "string" ? data.label : "";

  return (
    <div style={css}>
      <Handle type="target" position={Position.Top} />
      {sketch !== undefined ? (
        <SketchSvg
          width={sketch.width}
          height={sketch.height}
          paths={sketch.paths}
        />
      ) : (
        useSvg &&
        shape != null && (
          <ShapeOverlay shape={shape} cssStyleProps={cssStyleProps} />
        )
      )}
      <div
        style={suppressCssBox ? { position: "relative", zIndex: 1 } : undefined}
      >
        <NodeLabel label={label} description={description} />
        {inlineChildren && inlineChildren.length > 0 && (
          <InlineChildrenList>{inlineChildren}</InlineChildrenList>
        )}
      </div>
      <Handle type="source" position={Position.Bottom} />
    </div>
  );
}
